/**
 * The founder invite link.
 *
 * Canon § 5 records how a founder arrived — `invite` or `direct` — and the code in
 * `?invite=…` is what carries that. Decision `D-011` withdrew the $20/$10 split, so the code
 * no longer decides an amount: the signup grant is banded by approval sequence and an invited
 * founder is worth exactly what a direct one is. The source survives as attribution and as an
 * anti-abuse signal.
 *
 * Two rules still follow, and they pull in opposite directions:
 *
 *   - it must **survive the journey** from wherever the link landed to the submission the
 *     founder eventually sends, which may be minutes and several page loads later;
 *   - it must **not leak**, because canon `docs/SECURITY.md` is explicit that a value like
 *     this must not escape through referrer headers, screenshots or a shared address bar —
 *     and an attribution signal anyone can forge is an attribution signal worth nothing.
 *
 * So the code is lifted out of the URL into `sessionStorage` on first sight and the URL is
 * rewritten without it. `sessionStorage` rather than `localStorage`: the grant belongs to
 * the founder who followed the link in this tab, not to the browser forever.
 *
 * **Nothing here decides whether the code is valid.** It is compared against a server-side
 * secret in core (`src/lib/invite.ts` there) and can only ever be *presented* from the
 * browser. An unrecognised code does not fail a submission — core records the signup as
 * `direct` and says so in `inviteCodeRecognized`, which is what lets the page tell a
 * founder plainly that their link did not apply.
 */

export const INVITE_QUERY_PARAM = "invite";
export const INVITE_STORAGE_KEY = "dealbid.invite";

/**
 * The longest code core will accept (`optionalText(..., { max: 128 })`).
 *
 * Anything longer is treated as absent rather than forwarded. A value that cannot be a real
 * code would otherwise fail the whole submission with a validation error on a field the
 * founder cannot see or correct — a mistyped link must not be able to block a real deal.
 */
const MAX_CODE_LENGTH = 128;

/** Codes are opaque, but never contain whitespace or control characters. */
const CODE_PATTERN = /^[\x21-\x7e]{1,128}$/;

function isUsableCode(value: string): boolean {
  return value.length <= MAX_CODE_LENGTH && CODE_PATTERN.test(value);
}

/**
 * Storage can throw outright — Safari's private mode, a browser configured to block site
 * data — so every access is guarded. A visitor whose storage is unavailable simply keeps
 * the code for as long as the current page lives; the flow still works, and nothing here
 * is allowed to break a page over it.
 */
function readStored(): string | null {
  try {
    const stored = window.sessionStorage.getItem(INVITE_STORAGE_KEY);
    return stored !== null && isUsableCode(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStored(code: string): void {
  try {
    window.sessionStorage.setItem(INVITE_STORAGE_KEY, code);
  } catch {
    // Ignored on purpose — see readStored.
  }
}

/** In-memory fallback, so the code survives at least the current page without storage. */
let remembered: string | null = null;

/**
 * Takes the code out of the current URL, if there is one, and remembers it.
 *
 * Called once at startup. The rewrite uses `replaceState` rather than a navigation so the
 * visitor's history is not polluted with an entry they never chose, and so the back button
 * does not walk them back into a URL that still carries the code.
 */
export function captureInviteCode(): void {
  const url = new URL(window.location.href);
  const presented = url.searchParams.get(INVITE_QUERY_PARAM);
  if (presented === null) return;

  url.searchParams.delete(INVITE_QUERY_PARAM);
  try {
    window.history.replaceState(window.history.state, "", url.toString());
  } catch {
    // A rewrite that fails costs privacy, not correctness; the code is still captured.
  }

  const code = presented.trim();
  if (!isUsableCode(code)) return;

  remembered = code;
  writeStored(code);
}

/** The code to present with a submission, or `null` for an ordinary direct signup. */
export function readInviteCode(): string | null {
  return remembered ?? readStored();
}

/**
 * Forgets the code after it has been presented with an accepted submission.
 *
 * It has done its work at that point: canon § 5 records the signup source once, at signup,
 * so re-presenting it later can never change anything, and holding a secret for no further
 * purpose is the thing to avoid.
 */
export function clearInviteCode(): void {
  remembered = null;
  try {
    window.sessionStorage.removeItem(INVITE_STORAGE_KEY);
  } catch {
    // Ignored on purpose — see readStored.
  }
}
